import React, { useEffect, useState } from "react";
import "../styles/DSA.css";
import { Helmet } from "react-helmet";

const API_BASE = "https://codematrix.co.in/no-auth/api/users";

const BlockImage = ({ block }) =>
  block.imageUrl ? (
    <figure className="dsa-block-image">
      <img src={block.imageUrl} alt={block.imageAlt || ""} />
      {block.imageCaption && (
        <figcaption className="dsa-block-image__caption">
          {block.imageCaption}
        </figcaption>
      )}
    </figure>
  ) : null;

// Walks every lesson in this course, in order, and returns the id of the
// first one that has an MCQ quiz attached. Stops as soon as it finds one.
// NOTE: this checks lessons one request at a time, so on a course with a
// lot of lessons before its first quiz this can take a moment. If your
// topics API can return a `hasQuiz` flag on each child topic, prefer that
// instead and skip this lookup entirely — much cheaper.
const findFirstMcqChildId = async (topicsData) => {
  const children =
    topicsData?.subTopics?.flatMap((sub) => sub.childTopics || []) || [];

  for (const child of children) {
    try {
      const res = await fetch(`${API_BASE}/lessons/${child.id}`);
      if (!res.ok) continue;
      const result = await res.json();
      if (result?.data?.contentQuiz?.questions?.length) {
        return child.id;
      }
    } catch (err) {
      console.error("mcq lookup error:", err);
    }
  }
  return null;
};

export default function DSA({ id, startWithMcq }) {
  const [topics, setTopics] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [activeId, setActiveId] = useState(id);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [copied, setCopied] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({}); // { [subTopicId]: boolean }

  // topic tree — fetched once
  useEffect(() => {
    let cancelled = false;

    fetch(`${API_BASE}/topics/${id}`)
      .then((res) => res.json())
      .then(async (data) => {
        if (cancelled) return;
        setTopics(data.data);

        if (startWithMcq) {
          const mcqChildId = await findFirstMcqChildId(data.data);
          if (cancelled) return;
          const fallbackChild = data.data?.subTopics?.[0]?.childTopics?.[0];
          setActiveId(mcqChildId || fallbackChild?.id || null);
        } else {
          const firstChild = data.data?.subTopics?.[0]?.childTopics?.[0];
          if (firstChild) {
            setActiveId(firstChild.id);
          }
        }
      })
      .catch((err) => console.error("topics fetch error:", err));

    return () => {
      cancelled = true;
    };
  }, [id, startWithMcq]);

  // auto-expand whichever group contains the active lesson
  useEffect(() => {
    if (!topics?.subTopics) return;
    const parent = topics.subTopics.find((sub) =>
      sub.childTopics?.some((c) => c.id === activeId)
    );
    if (parent) {
      setExpandedGroups((prev) => ({ ...prev, [parent.id]: true }));
    }
  }, [topics, activeId]);

  // lesson content — refetched whenever the active sidebar item changes
  useEffect(() => {
    const fetchLesson = async () => {
      if (!activeId) return;
      try {
        const res = await fetch(`${API_BASE}/lessons/${activeId}`);
        if (!res.ok) throw new Error(`HTTP error: ${res.status}`);
        const result = await res.json();
        setLesson(result.data);
      } catch (err) {
        console.error("lesson fetch error:", err);
        setLesson(null);
      } finally {
        requestAnimationFrame(() => {
          const content = document.querySelector(".dsa-content");
          content?.scrollTo({ top: 0, behavior: "auto" });
          window.scrollTo({ top: 0, behavior: "auto" });
        });
      }
    };
    fetchLesson();
  }, [activeId]);

  useEffect(() => {
    setSelectedAnswers({});
  }, [activeId]);

  const handleLessonChange = (id) => {
    setActiveId(id);

    const content = document.querySelector(".dsa-content");
    if (content) {
      content.scrollTo({ top: 0, behavior: "smooth" });
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
    document.documentElement.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleGroup = (subId) => {
    setExpandedGroups((prev) => ({ ...prev, [subId]: !prev[subId] }));
  };

  const handleOptionClick = (questionId, optionId) => {
    if (selectedAnswers[questionId]) return;
    setSelectedAnswers((prev) => ({ ...prev, [questionId]: optionId }));
  };

  const handleCopy = (id, content) => {
    navigator.clipboard?.writeText(content || "");
    setCopied(id);
    setTimeout(() => setCopied(null), 1500);
  };

  const flatChildren =
    topics?.subTopics?.flatMap((sub) => sub.childTopics || []) || [];
  const currentIndex = flatChildren.findIndex((c) => c.id === activeId);
  const prevChild = currentIndex > 0 ? flatChildren[currentIndex - 1] : null;
  const nextChild =
    currentIndex >= 0 && currentIndex < flatChildren.length - 1
      ? flatChildren[currentIndex + 1]
      : null;

  const parentSub = topics?.subTopics?.find((sub) =>
    sub.childTopics?.some((c) => c.id === activeId)
  );

  const totalTopics = flatChildren.length;
  const progressPct =
    totalTopics > 0 ? Math.round(((currentIndex + 1) / totalTopics) * 100) : 0;

  let itemCounter = 0;

  // Reset everything when switching to a different course.
  useEffect(() => {
    setLesson(null);
    setTopics(null);
    setExpandedGroups({});
    setSelectedAnswers({});
    setActiveId(null);
  }, [id]);

  // after the lesson fetch succeeds, if this was an mcq-mode jump, scroll to the quiz
useEffect(() => {
  if (startWithMcq && lesson?.contentQuiz) {
    requestAnimationFrame(() => {
      document
        .querySelector(".dsa-quiz")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }
}, [lesson, startWithMcq]);

  return (
    <>
      <Helmet>
        <title>{lesson?.title || "DSA"}</title>
      </Helmet>

      <div className="dsa-layout">
        {/* Sidebar */}
        <aside className="dsa-sidebar">
          <div className="dsa-course-card">
            <div className="dsa-course-card__icon">
              {topics?.title ? topics.title.charAt(0).toUpperCase() : "•"}
            </div>
            <div className="dsa-course-card__meta">
              <h4>{topics?.title || "Course"}</h4>
              <span>{topics?.subtitle || "Beginner to Advanced"}</span>
            </div>
          </div>

          <nav className="dsa-nav-groups">
            {topics?.subTopics?.map((sub) => {
              const isOpen = !!expandedGroups[sub.id];
              const childCount = sub.childTopics?.length || 0;

              return (
                <div key={sub.id} className="dsa-sidebar-group">
                  <button
                    type="button"
                    className={`dsa-sidebar-group-title dsa-sidebar-group-title--toggle${
                      isOpen ? " open" : ""
                    }`}
                    onClick={() => toggleGroup(sub.id)}
                    aria-expanded={isOpen}
                  >
                    <span>{sub.title}</span>
                    <span className="dsa-sidebar-group-meta">
                      <span className="dsa-sidebar-group-count">
                        {childCount}
                      </span>
                      <span
                        className={`dsa-sidebar-group-arrow${
                          isOpen ? " open" : ""
                        }`}
                      >
                        ▾
                      </span>
                    </span>
                  </button>

                  <ul
                    className={`dsa-sidebar-group-list${isOpen ? " open" : ""}`}
                  >
                    {sub.childTopics?.map((child) => {
                      const isActive = child.id === activeId;
                      return (
                        <li
                          key={child.id}
                          className={isActive ? "active" : ""}
                          onClick={() => setActiveId(child.id)}
                        >
                          <span className="dsa-item-title">{child.title}</span>
                          {child.completed && (
                            <span className="dsa-item-check">✓</span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </nav>
        </aside>

        {/* Main content */}
        <main className="dsa-content">
          <div className="dsa-breadcrumb">
            <span>Learn</span>
            <span className="dsa-breadcrumb__sep">›</span>
            <span>{topics?.title}</span>
            {parentSub && (
              <>
                <span className="dsa-breadcrumb__sep">›</span>
                <span>{parentSub.title}</span>
              </>
            )}
            <span className="dsa-breadcrumb__sep">›</span>
            <span className="dsa-breadcrumb__current">{lesson?.title}</span>
          </div>

          {topics?.title && (
            <span className="dsa-pill dsa-pill--topic">{topics.title}</span>
          )}

          <h1 className="dsa-title">{lesson?.title}</h1>
          {lesson?.description && (
            <p className="dsa-lede">{lesson.description}</p>
          )}

          <div className="dsa-tag-row">
            {(lesson?.difficulty || lesson?.level) && (
              <span className="dsa-tag dsa-tag--level">
                🎓 {lesson.difficulty || lesson.level}
              </span>
            )}
            {(lesson?.readTimeMins || lesson?.readTime) && (
              <span className="dsa-tag">
                🕐{" "}
                {lesson.readTimeMins
                  ? `${lesson.readTimeMins} min`
                  : lesson.readTime}
              </span>
            )}
            <span className="dsa-tag">{"</>"} Examples</span>
            {lesson?.updatedAt && (
              <span className="dsa-tag">🗓 Updated {lesson.updatedAt}</span>
            )}
          </div>

          <div className="dsa-nav-buttons dsa-nav-buttons--top">
            <button
              disabled={!prevChild}
              onClick={() => prevChild && handleLessonChange(prevChild.id)}
            >
              &lt; Previous
            </button>
            <button
              disabled={!nextChild}
              onClick={() => nextChild && handleLessonChange(nextChild.id)}
            >
              Next &gt;
            </button>
          </div>

          {lesson?.contentBlocks?.map((block) => {
            switch (block.blockType) {
              case "list": {
                let listItems = [];
                try {
                  listItems = JSON.parse(block.content) || [];
                } catch (err) {
                  console.error("list block parse error:", err);
                }

                return (
                  <div className="dsa-block-wrap" key={block.id}>
                    {block.title && (
                      <h2 className="dsa-block-heading">{block.title}</h2>
                    )}
                    <ul className="dsa-block-list">
                      {listItems.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                    <BlockImage block={block} />
                  </div>
                );
              }

              case "heading":
                return (
                  <div className="dsa-block-wrap" key={block.id}>
                    <h2 className="dsa-block-heading">{block.title}</h2>
                    <BlockImage block={block} />
                  </div>
                );

              case "paragraph":
                return (
                  <div className="dsa-block-wrap" key={block.id}>
                    {block.title && (
                      <h2 className="dsa-block-heading">{block.title}</h2>
                    )}
                    <p className="dsa-block-paragraph">{block.content}</p>
                    <BlockImage block={block} />
                  </div>
                );

              case "code":
                return (
                  <div className="dsa-code-card" key={block.id}>
                    <div className="dsa-code-card__header">
                      <span>{block.language || "Python"}</span>
                      <div className="dsa-code-card__actions">
                        <button
                          className="dsa-copy-btn"
                          onClick={() => handleCopy(block.id, block.content)}
                        >
                          {copied === block.id ? "✓ Copied" : "⧉ Copy"}
                        </button>
                      </div>
                    </div>
                    <pre className="dsa-code-block">
                      <code>{block.content}</code>
                    </pre>
                    {block.codeOutput && (
                      <div className="dsa-code-output">{block.codeOutput}</div>
                    )}
                    <BlockImage block={block} />
                  </div>
                );

              case "note":
                return (
                  <div key={block.id} className="dsa-note-block">
                    <span className="dsa-note-block__icon">💡</span>
                    <div style={{ flex: 1 }}>
                      {block.title && <strong>{block.title}</strong>}
                      <p>{block.content}</p>
                      <BlockImage block={block} />
                    </div>
                  </div>
                );

              default:
                return null;
            }
          })}

          {lesson?.contentQuiz && (
            <div className="dsa-quiz">
              <h2>{lesson.contentQuiz.title}</h2>
              <p>{lesson.contentQuiz.description}</p>

              {lesson.contentQuiz.questions?.map((q, i) => {
                const answeredOptionId = selectedAnswers[q.id];
                const hasAnswered = Boolean(answeredOptionId);

                return (
                  <div key={q.id} className="dsa-quiz-question">
                    <p>
                      <strong>Q{i + 1}.</strong> {q.question}
                    </p>
                    <ul>
                      {q.options?.map((opt) => {
                        const isSelected = answeredOptionId === opt.id;
                        let liClass = "";
                        if (hasAnswered) {
                          if (isSelected && opt.isCorrect) liClass = "correct";
                          else if (isSelected && !opt.isCorrect)
                            liClass = "incorrect";
                          else if (!isSelected && opt.isCorrect)
                            liClass = "correct-reveal";
                        }

                        return (
                          <li
                            key={opt.id}
                            className={liClass}
                            onClick={() => handleOptionClick(q.id, opt.id)}
                          >
                            <span className="dsa-option-text">
                              {opt.optionKey}. {opt.optionText}
                            </span>
                            {isSelected && (
                              <span className="dsa-quiz-mark">
                                {opt.isCorrect ? "✓" : "✗"}
                              </span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                    {hasAnswered && (
                      <p className="dsa-quiz-explanation">{q.explanation}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="dsa-nav-buttons dsa-nav-buttons--bottom">
            <button
              className="dsa-nav-btn dsa-nav-btn--prev"
              disabled={!prevChild}
              onClick={() => prevChild && handleLessonChange(prevChild.id)}
            >
              <span className="dsa-nav-btn__arrow">&larr;</span>
              <span className="dsa-nav-btn__text">
                <span className="dsa-nav-btn__label">Previous</span>
                <span className="dsa-nav-btn__name">
                  {prevChild?.title || ""}
                </span>
              </span>
            </button>
            <button
              className="dsa-nav-btn dsa-nav-btn--next"
              disabled={!nextChild}
              onClick={() => nextChild && handleLessonChange(nextChild.id)}
            >
              <span className="dsa-nav-btn__text">
                <span className="dsa-nav-btn__label">Next</span>
                <span className="dsa-nav-btn__name">
                  {nextChild?.title || ""}
                </span>
              </span>
              <span className="dsa-nav-btn__arrow">&rarr;</span>
            </button>
          </div>
        </main>
      </div>
    </>
  );
}